import type { CaptureResult } from "posthog-js";

const EXPLICIT_SURFACES = [
    "search_results",
    "find_feed",
    "provider",
    "similar",
    "matching",
    "chat",
] as const;
const SURFACES = [...EXPLICIT_SURFACES, "excluded", "unknown"] as const;
type MeasurementSurface = (typeof SURFACES)[number];

function isOneOf<T extends string>(
    values: readonly T[],
    value: CaptureResult["properties"][string],
): value is T {
    return values.some((candidate) => candidate === value);
}

export function cardMeasurementSurface(
    explicit: string | undefined,
    pathname: string,
): MeasurementSurface {
    if (isOneOf(EXPLICIT_SURFACES, explicit)) return explicit;
    if (pathname === "/" || pathname === "/find") return "find_feed";
    if (pathname.startsWith("/provider/")) return "provider";
    if (pathname.startsWith("/dog/")) return "similar";
    if (pathname.startsWith("/match/")) return "matching";
    if (pathname.startsWith("/saved") || pathname.startsWith("/profile")) {
        return "excluded";
    }
    return "unknown";
}

function isUuid(value: CaptureResult["properties"][string]): value is string {
    return typeof value === "string" && /^[0-9a-f-]{36}$/i.test(value);
}

function safeSdkFields(source: CaptureResult["properties"]) {
    return {
        ...(typeof source.token === "string" ? { token: source.token } : {}),
        ...(typeof source.distinct_id === "string" ||
        typeof source.distinct_id === "number"
            ? { distinct_id: source.distinct_id }
            : {}),
        ...(isUuid(source.$session_id)
            ? { $session_id: source.$session_id }
            : {}),
        ...(isUuid(source.$window_id) ? { $window_id: source.$window_id } : {}),
        ...(typeof source.$host === "string" &&
        /^[a-z0-9.-]+(?::\d{1,5})?$/i.test(source.$host)
            ? { $host: source.$host }
            : {}),
        ...(typeof source.environment === "string" &&
        /^[a-z0-9_-]{1,32}$/i.test(source.environment)
            ? { environment: source.environment }
            : {}),
    };
}

export function scrubDogCardOpenedCapture(
    capture: CaptureResult | null,
): CaptureResult | null {
    if (!capture || capture.event !== "dog_card_opened") return capture;
    const source = capture.properties;
    if (
        !isUuid(source.eventId) ||
        !isUuid(source.presentationId) ||
        typeof source.listingId !== "string" ||
        source.listingId.length === 0 ||
        !["confirmed", "pending", "preview", "error", "ineligible"].includes(
            String(source.measurementStatus),
        )
    )
        return null;
    return {
        uuid: capture.uuid,
        event: capture.event,
        ...(capture.timestamp ? { timestamp: capture.timestamp } : {}),
        properties: {
            ...safeSdkFields(source),
            ...(isUuid(source.selectionId)
                ? { selectionId: source.selectionId }
                : {}),
            ...(isOneOf(SURFACES, source.surface)
                ? { surface: source.surface }
                : {}),
            eventId: source.eventId,
            listingId: source.listingId,
            presentationId: source.presentationId,
            measurementStatus: source.measurementStatus,
        },
    };
}
